import React, { useEffect, useRef, useState } from 'react';
import { DownIcon } from './assets/down-icon';
import { ExceptionState, ExpiredState } from './content-placeholder';
import { DownIcon2 } from './assets/down-icon2';
import { cn } from '@/lib/utils';

export type GenericWidgetListViewHandle = {
  scrollToBottom(): void;
};

export const GenericWidgetListView = React.forwardRef<GenericWidgetListViewHandle, {
  children: React.ReactNode[];
  isLoading?: boolean;
  showTemplates?: boolean;
  showException?: boolean;
  showExpired?: boolean;
  expiredAt?: string;
  scrollToBottomToken?: number;
  onRefresh?: () => Promise<void>;
  onLoadMore?: () => Promise<void>;
  onScrollStateChange?: (state: {
    direction: 'up' | 'down';
    nearTop: boolean;
    scrollTop: number;
  }) => void;
}>(({ children, isLoading, showException, showExpired, expiredAt, onLoadMore, onScrollStateChange, scrollToBottomToken }) => {
  const ref = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);
  const prevScrollTopRef = useRef(0);
  const scrollThrottleTimerRef = useRef<number | null>(null);
  const scrollThrottleMs = 200;
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    ref.current.scrollTop = 0;
  }, [scrollToBottomToken]);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const onScroll = async () => {
      const prev = prevScrollTopRef.current;
      const curr = el.scrollTop;
      const direction: 'up' | 'down' = curr < prev ? 'up' : 'down';

      const distanceToScrollMax = el.scrollHeight - el.clientHeight + el.scrollTop;
      if (distanceToScrollMax <= 200 && !loadingMoreRef.current && onLoadMore) {
        loadingMoreRef.current = true;
        const prevScrollHeight = el.scrollHeight;
        const prevScrollTop = el.scrollTop;
        try {
          await onLoadMore();
        } finally {
          const diff = el.scrollHeight - prevScrollHeight;
          el.scrollTop = prevScrollTop + diff;
          loadingMoreRef.current = false;
        }
      }

      scrollThrottleTimerRef.current = window.setTimeout(() => {
        const distanceToScrollMax = el.scrollTop;
        const isNearTop = distanceToScrollMax >= -240;
        if (isNearTop) {
          // onScrollStateChange?.({ direction, nearTop: isNearTop, scrollTop: el.scrollTop });
        } else if (Math.abs(curr - prev) >= 240) {
          onScrollStateChange?.({ direction, nearTop: isNearTop, scrollTop: el.scrollTop });
        }
        setShowScrollDown(!isNearTop);
        prevScrollTopRef.current = el.scrollTop;
        scrollThrottleTimerRef.current = null;
      }, scrollThrottleMs);
    };

    prevScrollTopRef.current = el.scrollTop;

    el.addEventListener('scroll', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (scrollThrottleTimerRef.current) window.clearTimeout(scrollThrottleTimerRef.current);
      scrollThrottleTimerRef.current = null;
    };
  }, [onLoadMore, onScrollStateChange]);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const updateOverflowState = () => {
      setHasOverflow(el.scrollHeight > el.clientHeight + 1);
    };

    updateOverflowState();

    window.addEventListener('resize', updateOverflowState);
    return () => {
      window.removeEventListener('resize', updateOverflowState);
    };
  }, [children, showException, showExpired, isLoading]);

  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-1 flex-col-reverse items-center gap-5 pb-1 overflow-y-auto ml-4',
        isLoading && '-ml-20',
        !hasOverflow && 'mt-5',
      )}
    >
      <div className='mt-auto'></div>
      {/* <div className='flex flex-1  flex-col-reverse items-start gap-5 w-[680px] pl-7'> */}
      {showExpired && <ExpiredState expiredAt={expiredAt} />}
      {showException && <ExceptionState />}
      {children}
      {/* </div> */}
      {showScrollDown && (
        <div className="sticky mb-5 top-full left-0 right-0 z-20 flex justify-center -translate-y-full pointer-events-none ">
          <button
            type="button"
            className="pointer-events-auto flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-[0px_0px_18px_4.5px_rgba(16,24,40,0.16),_0px_12px_24px_-6px_rgba(16,24,40,0.18)]"
            aria-label="Scroll to bottom"
            onClick={() => {
              const el = ref.current;
              if (!el) return;
              el.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <DownIcon2 className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
});
